/**
 * Host-neutral request/response contract for Borgee client domain calls.
 *
 * A transport owns credentials and delivery. Values are deliberately data-only
 * so the same contract can cross a Webview or renderer IPC boundary; Fetch API
 * conversion lives only in the Web adapter.
 */
export interface MultipartTextPart {
  name: string;
  value: string;
}

export interface MultipartFilePart {
  name: string;
  file: {
    filename: string;
    contentType?: string;
    bytes: Uint8Array;
  };
}

export interface MultipartBody {
  kind: 'multipart';
  parts: Array<MultipartTextPart | MultipartFilePart>;
}

export type BorgeeRequestBody = { kind: 'json'; value: string } | MultipartBody;

export type BorgeeResponseType = 'json' | 'bytes' | 'empty';

export interface ApiRequest {
  path: string;
  method?: string;
  headers?: Record<string, string>;
  body?: BorgeeRequestBody;
  responseType?: BorgeeResponseType;
  signal?: AbortSignal;
}

interface ApiResponseBase {
  status: number;
  statusText: string;
  headers: Record<string, string>;
}

export interface ApiJsonResponse extends ApiResponseBase {
  kind: 'json';
  data: unknown;
}

export interface ApiBytesResponse extends ApiResponseBase {
  kind: 'bytes';
  bytes: Uint8Array;
}

export interface ApiEmptyResponse extends ApiResponseBase {
  kind: 'empty';
}

/** A response payload is tied to its declared request response type. */
export type ApiResponse = ApiJsonResponse | ApiBytesResponse | ApiEmptyResponse;

export interface BorgeeTransport {
  request(request: ApiRequest): Promise<ApiResponse>;
}

let transport: BorgeeTransport | undefined;

export function configureBorgeeTransport(nextTransport: BorgeeTransport): void {
  transport = nextTransport;
}

export function getBorgeeTransport(): BorgeeTransport {
  if (!transport) {
    throw new Error('Borgee transport is not configured. A host adapter must configure it before using the client core.');
  }
  return transport;
}

/** Test-only reset so a test cannot accidentally depend on a previous host. */
export function resetBorgeeTransportForTests(): void {
  transport = undefined;
}
